import { Link, Grid, Typography, Divider, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Modal, Avatar, IconButton, Backdrop } from "@mui/material";
import Spinner from "../../common/Spinner";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MedicalCheckupDto, emptyMedicalCheckup } from "../../../models/medicalCheckups/medicalCheckupDto";
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import { MedicalCheckupResult, MedicalCheckupResultUtil } from "../../../models/enums/medicalCheckupResult";
import { ROUTES } from "../../../constants/routes";
import { DateUtils } from "../../../helpers/dateUtils";
import { UserUtils } from "../../../helpers/userUtils";
import { Gender, getGenderString } from "../../../models/enums/gender";
import { MedicalCheckupTypeUtil } from "../../../models/enums/medicalCheckupType ";
import ReactPlayer from "react-player";
import { DriverMedicalCheckupsHistoryModal } from "./DriverMedicalCheckupsHistoryModal";
import maleAvatar from '../../../assets/images/male-avatar.png';
import femaleAvatar from '../../../assets/images/female-avatar.png';
import { MedicalCheckupStatus } from "../../../models/enums/medicalCheckupStatus";
import DenyButton from "../../common/buttons/DenyButton";
import AcceptButton from "../../common/buttons/AcceptButton";
import DenyLabel from "../../common/labels/DenyLabel";
import AcceptLabel from "../../common/labels/AcceptLabel";
import TmMedParamsLabel, { TmMedParamsType } from "../../common/labels/TmMedParamsLabel";
import { RoleType } from "../../../models/enums/roleType";
import { ShowForRole } from "../../common/permissions/ShowForRole";
import { medicalCheckupsService } from "../../../services/medicalCheckupsService";
import { permissionService } from "../../../services/permissionService";
import { ShowForRoles } from "../../common/permissions/ShowForRoles";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DocumentMenu from "./DocumentMenu";
import { medicsService } from "../../../services/medicsService";
import { TokenType } from "../../../models/enums/tokenType";
import { MedicDigitalSignDto } from "../../../models/medics/medicDigitalSignDto";
import { CloudTokenSigningButtons } from "./CloudTokenSigningButtons";
import { PhysicalTokenSigningButtons } from "./PhysicalTokenSigningButtons";
import { WorkplaceNotReadyDialog } from "../../common/WorkplaceNotReadyDialog";
import { ValidationErrorDialog } from "../../common/ValidationErrorDialog";
import { cryptoProService } from "../../../services/cryptoProService";

export const MedicalCheckupDetails = () => {
    const [loading, setLoading] = useState(true);
    const [spinnerText, setSpinnerText] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const [workplaceError, setWorkplaceError] = useState<string | null>(null);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const [workplaceCheckLoading, setWorkplaceCheckLoading] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [medicalCheckupId, setMedicalCheckupId] = useState<number>();
    const [medicalCheckup, setMedicalCheckup] = useState<MedicalCheckupDto>(emptyMedicalCheckup);
    const [defaultCertificate, setDefaultCertificate] = useState<MedicDigitalSignDto | null>(null);
    const [certificateType, setCertificateType] = useState<TokenType | null>(null);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        const id = Number(params.id);
        if (!id) return;

        setMedicalCheckupId(id);

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            medicalCheckupsService.unlock(id);
        };

        let checkupStatus: MedicalCheckupStatus | null = null;

        const fetchMedicalCheckupData = async () => {
            try {
                setLoading(true);
                const response = await medicalCheckupsService.getById(id);
                setMedicalCheckup(response.data.data);
                checkupStatus = response.data.data.status;

                // Lock only on behalf of medic and only new medical checkups
                if (permissionService.hasRole(RoleType.Medic) && checkupStatus === MedicalCheckupStatus.New) {
                    medicalCheckupsService.lock(id);
                    window.addEventListener('beforeunload', handleBeforeUnload);
                }
            } catch (e: any) {
                console.error(e);
                if (e.response && e.response.status === 404) {
                    navigate(ROUTES.auth.notFound);
                } else {
                    setNetworkError(e.response?.data?.message ?? e.message ?? "Не удалось загрузить данные медосмотра");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchMedicalCheckupData();

        // Unmount component function
        return () => {
            if (permissionService.hasRole(RoleType.Medic) && checkupStatus === MedicalCheckupStatus.New) {
                medicalCheckupsService.unlock(id);
            }
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [params.id, navigate]);

    // Проверяем тип сертификата медика при загрузке
    useEffect(() => {
        const checkCertificateType = async () => {
            if (!permissionService.hasRole(RoleType.Medic)) {
                setCertificateType(null);
                return;
            }

            try {
                // Получаем сертификат по умолчанию
                const defaultCert = await medicsService.getMedicCertificate();
                setDefaultCertificate(defaultCert ?? null);
                setCertificateType(defaultCert?.tokenType ?? null);
            } catch (e: any) {
                // Сертификат не найден или другая ошибка
                console.error('Ошибка при проверке сертификата:', e);
                setDefaultCertificate(null);
                setCertificateType(null);
            }
        };

        checkCertificateType();
    }, []);


    function renderCheckupResult(result: MedicalCheckupResult) {
        const strValue = MedicalCheckupResultUtil.getStringValue(result);
        let color = result === MedicalCheckupResult.Rejected ? '#ec4a71' : '#5bba64';
        let backgroundColor = result === MedicalCheckupResult.Rejected ? '#fce4ea' : '#edfbee';

        return <Box display="inline-block" color={color} sx={{ backgroundColor: { backgroundColor }, borderRadius: "25px", paddingLeft: "15px", paddingRight: "15px", fontWeight: "bold" }}>{strValue}</Box>;
    };

    function renderComplaints(complaints: string[]) {
        if (complaints && complaints.length > 0) {
            return complaints.map((complaint, index) => (
                <div key={index} style={{ marginBottom: "8px" }}><DenyLabel>{complaint}</DenyLabel></div>
            ))
        }
        return <AcceptLabel>Нет жалоб</AcceptLabel>;
    }

    const toggleHistoryModal = () => {
        setIsHistoryModalOpen(!isHistoryModalOpen);
    };

    const [open, setOpen] = useState(false);
    const [image, setImage] = useState("false");

    const handleClose = () => {
        setOpen(false);
    };

    const handleImage = (value: any) => {
        setImage(value);
        setOpen(true);
    };

    function renderSectionHeader(title: string, add: string = "", my: number = 1) {
        if (!!add) {
            add = ` ${add}`
            add = ` ${add}`
        }

        return (
            <Typography variant="h6" sx={{ fontWeight: 'bold', my: my }}>
                {title}{add}
            </Typography>
        );
    }

    function renderSectionItem(label: string, content: React.ReactNode, cn: string = "") {
        if (cn === "") {
            cn = "ltb"
        }

        return (
            <div>
                <Typography className={cn} variant="body1">{label}</Typography>
                &nbsp; <Typography className="rtb">{content}</Typography>
            </div>
        );
    }

    const MedicalCheckupTable = () => {
        return (
            <>
                <TableContainer sx={{ mr: 20, maxWidth: '95%', border: 0, px: 3, overflow: "hidden" }}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow sx={{ borderTop: 1, borderColor: "#e7e7ea" }}>
                                <TableCell className="grey">Давление</TableCell>
                                <TableCell className="grey">Пульс</TableCell>
                                <TableCell className="grey">Алкоголь, мг/л</TableCell>
                                <TableCell className="grey">Температура</TableCell>
                                <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                    <TableCell className="grey">Результат авто</TableCell>
                                </ShowForRoles>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TmMedParamsLabel type={TmMedParamsType.bloodPressure} medicalCheckup={medicalCheckup} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TmMedParamsLabel type={TmMedParamsType.pulse} medicalCheckup={medicalCheckup} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TmMedParamsLabel type={TmMedParamsType.alcohol} medicalCheckup={medicalCheckup} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TmMedParamsLabel type={TmMedParamsType.temperature} medicalCheckup={medicalCheckup} />
                                </TableCell>
                                <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                    <TableCell sx={{ fontWeight: "bold" }}>{renderCheckupResult(medicalCheckup.autoResult)}</TableCell>
                                </ShowForRoles>
                            </TableRow>
                        </TableBody>
                    </Table>

                </TableContainer>
                <DriverMedicalCheckupsHistoryModal
                    open={isHistoryModalOpen}
                    onClose={toggleHistoryModal}
                    driverId={medicalCheckup.driver.id!}
                    driverFio={UserUtils.getFio(medicalCheckup.driver.user)}
                />
            </>
        );
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            // If there is a history, navigate back
            navigate(-1);
        } else {
            // If opened in a new tab, navigate to the desired route
            navigate(ROUTES.medicalCheckups.history);
        }
    };

    return (
        <div>
            {loading
                ? (
                    <Backdrop
                        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                        open={loading}
                    >
                        <Spinner text={spinnerText} />
                    </Backdrop>
                )
                : !medicalCheckup.id
                ? (
                    <Box py={8} textAlign="center">
                        <Typography variant="h6" fontWeight="bold">Не удалось загрузить данны медосмотра</Typography>
                        <Typography color="text.secondary" mt={1}>Вернитесь в очередь и повторите попытку.</Typography>
                    </Box>
                )
                : (<Box>
                    <Grid container mt={1} justifyContent="space-between" alignItems="center">
                        <Grid item xs="auto">
                            <IconButton onClick={handleBack}>
                                <Avatar sx={{ backgroundColor: "white", border: 1, borderColor: "#9588e8" }}>
                                    <ChevronLeftIcon sx={{ color: '#9588e8' }} />
                                </Avatar>
                            </IconButton>
                        </Grid>
                        <Grid item xs="auto" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="h6" align="center">Данные медицинского осмотра</Typography>
                        </Grid>
                        <Grid item xs="auto">
                            <DocumentMenu medicalCheckupId={medicalCheckupId!} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} paddingTop={0}>
                        <Grid item xs={12} md={8}>
                            <Box className="gbr">
                                <Grid container paddingBottom={2} pl={2} pt={1}>
                                    <Grid item xs={3} md={3} textAlign={"center"}>
                                        <Typography className="grey">Фото сотрудника</Typography>
                                        {(medicalCheckup.driver.user?.photoUrl)
                                            ? (
                                                <img
                                                    src={medicalCheckup.driver.user?.photoUrl}
                                                    alt='Фото водителя'
                                                    onClick={(e) => handleImage(medicalCheckup.driver.user?.photoUrl)}
                                                    style={{ maxHeight: '140px', borderRadius: "25px" }}
                                                />
                                            )
                                            : (<Avatar
                                                alt="Фото водителя"
                                                src={(medicalCheckup.driver.user.gender === Gender.Male ? maleAvatar : femaleAvatar)}
                                                sx={{ width: 100, height: 100 }}
                                            />)}
                                    </Grid>
                                    <Grid item xs={3} md={3} textAlign={"center"}>
                                        <Typography className="grey">Фото осмотра</Typography>
                                        {(medicalCheckup.photoUrl) ? (
                                            <img
                                                src={medicalCheckup.photoUrl}
                                                alt='Фото медосмотра'
                                                onClick={(e) => handleImage(medicalCheckup.photoUrl)}
                                                style={{ maxHeight: '140px', borderRadius: "25px" }}
                                            />
                                        )
                                            : (<WallpaperIcon sx={{ fontSize: 100 }} />)}
                                    </Grid>
                                    <Grid item xs={4} md={4}>
                                        <Box sx={{ float: "left", clear: "left" }}>
                                            {renderSectionHeader(UserUtils.getFio(medicalCheckup.driver.user), "", 0)}
                                            {renderSectionItem("Табельный №", medicalCheckup.driver.employeeNumber, "ltb-p0")}
                                            {renderSectionItem("Пол", getGenderString(medicalCheckup.driver.user?.gender), "ltb-p0")}
                                            {renderSectionItem("Год рождения", `${DateUtils.getYear(medicalCheckup.driver.user?.birthDate)} (${DateUtils.getAge(medicalCheckup.driver.user?.birthDate)})`, "ltb-p0")}
                                            {renderSectionItem("Клиент", medicalCheckup.company.name, "ltb-p0")}
                                            {/* <hr style={{marginTop: 10, border: "none", height: "1px"}} color="#e5e5e9"></hr>
                                            {renderSectionItem("Ответственное лицо", "Иванов Алексей Александрович", "ltb-p0")}
                                            {renderSectionItem("", "+7 977 543-24-23")} */}
                                        </Box>
                                    </Grid>

                                    {/* Подпись */}
                                    <Grid item xs={2} md={2}>
                                        <Box pt={0} pr={2} sx={{ float: "right", clear: "right" }}>
                                            {/* <Box className="gbr" pt={1} pl={2} pb={1}> */}
                                            <Box pt={1} pl={2} pb={1}>
                                                <Box className="rtb" height={80}>{medicalCheckup.signatureUrl
                                                    ? (
                                                        <img
                                                            src={medicalCheckup.signatureUrl}
                                                            alt='Подпись водителя'
                                                            onClick={(e) => handleImage(medicalCheckup.signatureUrl)}
                                                            style={{ height: '80px', width: "auto", maxWidth: "200px" }}
                                                        />
                                                    )
                                                    : (<WallpaperIcon />)}</Box>

                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box className="grey-block" marginTop={2}>
                                <Grid container spacing={2} paddingLeft={2} paddingBottom={2}>
                                    <Grid item xs={7} md={7}>
                                        {renderSectionHeader("Данные медосмотра")}
                                        {renderSectionItem("Тип осмотра", MedicalCheckupTypeUtil.getStringValue(medicalCheckup.type), "ltb-p0")}
                                    </Grid>
                                    <Grid item xs={5} md={5}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', my: 1, visibility: "hidden" }}>trim</Typography>
                                        {renderSectionItem("Дата осмотра",
                                            DateUtils.formatDateTimeWithTimeZone(medicalCheckup.startedAt, medicalCheckup.timeShift) + " - " + DateUtils.formatDateTimeWithTimeZone(medicalCheckup.endAt, medicalCheckup.timeShift), "ltb-p0"
                                        )}
                                    </Grid>

                                </Grid>

                                <MedicalCheckupTable />


                                <Grid container spacing={2} paddingLeft={2} pb={1}>
                                    <Grid item xs={8} md={8}>
                                        <Link href="#" py={2} px={2} sx={{ float: "left" }} display="flex" fontWeight="bold" underline="none" color="#9588e8" onClick={toggleHistoryModal}>Посмотреть историю медосмотров</Link>
                                    </Grid>
                                    <ShowForRole role={RoleType.Medic}>
                                        <Grid item xs={4} md={4}>
                                            {(medicalCheckup.status === MedicalCheckupStatus.New || medicalCheckup.status === MedicalCheckupStatus.SigningError) && (
                                                <>
                                                    {certificateType === TokenType.Cloud && (
                                                        <CloudTokenSigningButtons
                                                            medicalCheckup={medicalCheckup}
                                                            disabled={loading}
                                                            onValidationError={(message) => setValidationError(message)}
                                                            onWorkplaceError={(message) => setWorkplaceError(message)}
                                                            onNetworkError={(message) => setNetworkError(message)}
                                                            onLoadingChange={(loading, spinnerText) => {
                                                                setLoading(loading);
                                                                if (spinnerText) setSpinnerText(spinnerText);
                                                            }}
                                                        />
                                                    )}
                                                    {certificateType === TokenType.Physical && (
                                                        <PhysicalTokenSigningButtons
                                                            medicalCheckup={medicalCheckup}
                                                            disabled={loading}
                                                            defaultCertificate={defaultCertificate}
                                                            onValidationError={(message) => setValidationError(message)}
                                                            onWorkplaceError={(message) => setWorkplaceError(message)}
                                                            onNetworkError={(message) => setNetworkError(message)}
                                                            onLoadingChange={(loading, spinnerText) => {
                                                                setLoading(loading);
                                                                if (spinnerText) setSpinnerText(spinnerText);
                                                            }}
                                                        />
                                                    )}
                                                    {certificateType === null && (
                                                        <Box paddingTop={1} px={2} sx={{ display: 'flex', float: 'right' }}>
                                                            <Box px={2}>
                                                                <DenyButton onClick={() => {}} disabled>Недопуск</DenyButton>
                                                            </Box>
                                                            <Box>
                                                                <AcceptButton onClick={() => {}} disabled>Допуск</AcceptButton>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    </ShowForRole>
                                </Grid>

                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4} marginLeft={0}>
                            <Box className="gbr">
                                <Box px={2} pb={1}>
                                    {renderSectionHeader("Наличие жалоб")}
                                    {renderComplaints(medicalCheckup.complaints)}
                                </Box>
                            </Box>

                            {/* Справки */}
                            <Box p={1}></Box>
                            <Box className="gbr">
                                <Grid container spacing={2} p={2}>
                                    <Grid item xs={6} md={6}>
                                        <Typography variant="h6" fontWeight='bold'>Справки</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        {
                                            // medicalCheckup.driver.healthCertificates && medicalCheckup.driver.healthCertificates.length > 0
                                            //     ? <Button variant="contained" color="success" sx={{ margin: 1 }}>Посмотреть все</Button>
                                            //     : ""
                                        }
                                    </Grid>
                                    {
                                        medicalCheckup.driver.healthCertificates && medicalCheckup.driver.healthCertificates.length > 0
                                            ? medicalCheckup.driver.healthCertificates.map((h, index) => (
                                                <Grid item xs={6} md={6} key={index} height={302}>
                                                    {(h.photoFile || h.photoUrl) && (
                                                        <img
                                                            src={h.photoUrl}
                                                            alt='Health Certificate'
                                                            onClick={(e) => handleImage(h.photoUrl)}
                                                            style={{ maxHeight: '186px', borderRadius: "25px" }}
                                                        />

                                                    )}
                                                    <Typography variant="body1" fontWeight="bold">{h.type.name}</Typography>
                                                    <Typography variant="body1" fontWeight="bold" className="grey">Дата выдачи:</Typography>
                                                    <Typography variant="body1" fontWeight="bold">{DateUtils.formatDate(h.issued)}</Typography>
                                                </Grid>
                                            ))
                                            : <Typography variant="body1">{"<Нет справок>"}</Typography>
                                    }
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} paddingTop={0}>
                        <Grid item xs={12} md={12} marginLeft={0}>
                            <Box pl={2} pr={2} pt={2}>
                                <Grid container spacing={2} pb={2}>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="h6" fontWeight='bold' paddingBottom="10px">Запись осмотра</Typography>
                                        <div className='player-wrapper'>
                                            <ReactPlayer
                                                className='react-player'
                                                width="100%"
                                                height="100%"
                                                url={medicalCheckup.videoUrl}
                                                controls={true}
                                                playing={true} />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="h6" fontWeight='bold' paddingBottom="10px" sx={{ visibility: "hidden" }}>trim</Typography>
                                        <div className='player-wrapper'>
                                            <ReactPlayer
                                                className='react-player'
                                                width="100%"
                                                height="100%"
                                                url={medicalCheckup.video2Url}
                                                controls={true}
                                                playing={true} />
                                        </div>
                                    </Grid>

                                    <Grid item xs={12} md={4} marginLeft={0} pr={0}>
                                        {/* Норма медицинских показателей */}
                                        <Typography variant="h6" fontWeight='bold' paddingBottom="10px">Норма медицинских показателей</Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Box className="grey-block" p={2} pt={4}>
                                                    <Typography variant="body1" fontWeight="bold">Давление верхнее</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} md={6}>
                                                            <Typography variant="body1" className="grey">Мин.</Typography>
                                                            <Typography variant="body1" fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.upperPressureMin}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6} md={6}>
                                                            <Typography variant="body1" className="grey">Макс.</Typography>
                                                            <Typography variant="body1" fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.upperPressureMax}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Box className="grey-block" p={2} pt={4}>
                                                    <Typography variant="body1" fontWeight="bold">Давление нижнее</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body1" className="grey">Мин.</Typography>
                                                            <Typography variant="body1" fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.lowerPressureMin}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body1" className="grey">Макс.</Typography>
                                                            <Typography variant="body1" fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.lowerPressureMax}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2} pt={2}>

                                            <Grid item xs={12} md={6}>
                                                <Box className="grey-block" p={2}>
                                                    <Typography variant="body1" fontWeight="bold">Пульс</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body1" className="grey">Мин.</Typography>
                                                            <Typography fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.pulseMin}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body1" className="grey">Макс.</Typography>
                                                            <Typography fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.pulseMax}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Box className="grey-block" p={2}>
                                                    <Typography variant="body1" fontWeight="bold">Температура</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="body1" className="grey">Макс.</Typography>
                                                            <Typography fontWeight='bold'>{medicalCheckup.driver.defaultIndicators.temperatureMax}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            {/* {empty for grid formatting} */}
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                            </Box>
                        </Grid>

                        <Grid item xs={12} md={12} marginLeft={0}>

                            {/* Клиент */}
                            <Box className="gbr" pl={2} pb={2}>
                                <Grid container spacing={2} paddingTop={2}>
                                    <Grid item xs={12} md={8} marginLeft={0}>
                                        {renderSectionHeader("Клиент", medicalCheckup.company.name)}
                                        <Typography className="ltb" variant="body1" pb={2.5}>Диспетчеры</Typography>

                                        <Grid container spacing={2} paddingLeft={2} paddingBottom={1} textAlign="center">
                                            {medicalCheckup.company.dispatchers && medicalCheckup.company.dispatchers.length > 0
                                                ? medicalCheckup.company.dispatchers.map((d, index) => (
                                                    <Box key={index} className="vbr" px={2} py={1} m={0.5}>
                                                        <Typography variant="body1" fontWeight="bold">{UserUtils.getFio(d)}</Typography>
                                                        <Typography variant="body1">{d.phoneNumber ?? "<Нет телефона>"}</Typography>
                                                    </Box>
                                                ))
                                                : <Typography variant="body1">{"<Нет диспетчеров>"}</Typography>}
                                        </Grid>

                                    </Grid>
                                </Grid>
                            </Box>

                            <Box p={1}></Box>

                            {/* Результат */}
                            {medicalCheckup.status === MedicalCheckupStatus.Verdict && (
                                <Box className="gbr" pl={2} pb={2}>

                                    <div>
                                        {renderSectionHeader("Результат")}
                                        {renderSectionItem("Медик", <span style={{ textDecoration: 'underline' }}>{medicalCheckup.verdictMedicFio || UserUtils.getFio(medicalCheckup.medic?.user) || "-"}</span>)}
                                        {renderSectionItem("Медицинская организация", medicalCheckup.verdictOrganizationName || "-")}
                                        {renderSectionItem("Результат (автоматический)", renderCheckupResult(medicalCheckup.autoResult))}
                                        {renderSectionItem("Результат (медик)", renderCheckupResult(medicalCheckup.medicResult))}
                                        {renderSectionItem("Причины не допуска", medicalCheckup.rejectReasons ? medicalCheckup.rejectReasons.map((r, index) => r.name).join(', ') : "-")}
                                        {renderSectionItem("Комментарий", medicalCheckup.notAllowedDescription)}
                                    </div>
                                </Box>)}
                            {medicalCheckup.status === MedicalCheckupStatus.Verdict && (<Box p={1}></Box>)}

                            <Grid container spacing={2}>

                                <Grid item xs={12} md={6}>
                                    <Box className="gbr" pl={2} pb={2} sx={{ height: '200px', display: 'flex', flexDirection: 'column' }}>
                                        {renderSectionHeader("Дополнительная информация")}
                                        <Grid container sx={{ flex: 1 }}>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Время результата", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>
                                                    {medicalCheckup.signedDate
                                                        ? DateUtils.formatDateTimeWithTimeZone(medicalCheckup.signedDate, medicalCheckup.timeShift)
                                                        : DateUtils.formatDateTimeWithTimeZone(medicalCheckup.endAt, medicalCheckup.timeShift)
                                                    }
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Терминал", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>{medicalCheckup.terminal ? medicalCheckup.terminal.name : "<Нет терминала>"}</Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Адрес терминала", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>{medicalCheckup.terminal?.address ? medicalCheckup.terminal?.address : "<Нет адреса>"}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box className="gbr" pl={2} pb={2} sx={{ height: '200px', display: 'flex', flexDirection: 'column' }}>
                                        {renderSectionHeader("Показатели условий окружающей среды")}
                                        <Grid container sx={{ flex: 1 }}>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Температура, C", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>{medicalCheckup.terminalEnvironment?.temperature ?? "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Влажность, %", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>{medicalCheckup.terminalEnvironment?.humidity ?? "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                {renderSectionItem("Освещенность, Лк", "", "ltb")}
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography className="rtb" mt={'10px'}>{medicalCheckup.terminalEnvironment?.light ?? "-"}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>

                            </Grid>

                            <Grid container spacing={2}>

                                <Grid item xs={12} md={12}>
                                    <Box className="gbr" pl={2} pb={2} mt={2}>
                                        {renderSectionHeader("Хронология")}
                                        <TableContainer
                                            sx={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                overflowX: 'hidden',
                                                borderRadius: '10px'
                                            }}
                                        >
                                            <Table
                                                size="small"
                                                stickyHeader
                                                sx={{
                                                    width: '100%',
                                                    tableLayout: 'fixed',
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Этап медосмотра</TableCell>
                                                        <TableCell>Время начала</TableCell>
                                                        <TableCell>Время завершения</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {medicalCheckup.timings?.map((row, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{row.name}</TableCell>
                                                            <TableCell>{DateUtils.formatDateTimeWithTimeZone(row.startDateTime, medicalCheckup.timeShift)}</TableCell>
                                                            <TableCell>{DateUtils.formatDateTimeWithTimeZone(row.endDateTime, medicalCheckup.timeShift)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>


                    <Grid container spacing={2} padding={2}>
                        <Grid item xs={6} md={6}>

                            <Modal
                                className='modal-img'
                                open={open}
                                onClose={handleClose}
                                closeAfterTransition
                            >
                                <img
                                    src={image}
                                    alt='Health Certificate'
                                    style={{ maxHeight: "90%", maxWidth: "90%" }}
                                />
                            </Modal>
                        </Grid>
                    </Grid>
                    <Divider />

                </Box>)}
            {validationError && (
                <ValidationErrorDialog
                    open
                    message={validationError}
                    onClose={() => setValidationError(null)}
                />
            )}
            {networkError && (
                <ValidationErrorDialog
                    open
                    title="Ошибка связи"
                    message={networkError}
                    onClose={() => setNetworkError(null)}
                />
            )}
            {workplaceError && (
                <WorkplaceNotReadyDialog
                    open
                    message={workplaceError}
                    loading={workplaceCheckLoading}
                    onClose={() => setWorkplaceError(null)}
                    onRetry={async () => {
                        setWorkplaceCheckLoading(true);
                        try {
                            const wpStatus = await cryptoProService.checkWorkplaceReadiness();
                            if (wpStatus.isReady) {
                                setWorkplaceError(null);
                            } else {
                                setWorkplaceError(wpStatus.errors ?? 'Рабочее место не готово к подписанию');
                            }
                        } finally {
                            setWorkplaceCheckLoading(false);
                        }
                    }}
                />
            )}
        </div>
    );
}
